import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { CsPage, NavController } from 'providers/nav-controller';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { SettingsService } from 'services/settings.service';
import { formatMeasure } from 'util/formatting';
import { getTransitionSlideInOutMs } from 'services/browser-utils';
import {
  NutritionUpdateChoice,
  UpdatePlanOptionsPageParams,
  UpdatePlanOptionsPageResult,
  WorkoutsUpdateChoice,
} from './update-plan-options-interface';

import { LL } from 'i18n/i18n.service';

/** gap between the top of the action buttons and the floating chevron */
const SCROLL_HINT_GAP_PX = 12;
/** used only until the action buttons have been measured */
const DEFAULT_SCROLL_HINT_BOTTOM_PX = 88;
/** added to the bar's slide-in duration before the second measurement, to clear its tail */
const SETTLE_GRACE_MS = 100;

/**
 * Asked when the user saves changes in the intake form's change mode: what
 * should happen to the training program, and what should happen to the daily
 * calorie target.
 *
 * It replaces a single three-button alert that carried both decisions at once
 * (#4973). That alert never said the words nutrition or calories, so a user
 * answering it about their workouts could not tell they were also answering
 * about their food — and the daily calorie target they had typed in themselves
 * was thrown away without being mentioned.
 *
 * The page only reports the two answers. Everything the answers mean happens in
 * the intake form, which owns the plan being edited.
 */
@Component({
  selector: 'page-update-plan-options',
  templateUrl: 'update-plan-options.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: ModalPageMarker, useValue: new ModalPageMarker({ width: 480, height: 'auto' }) }],
})
export class UpdatePlanOptionsPage extends CsPage<UpdatePlanOptionsPageParams, UpdatePlanOptionsPageResult | undefined> {
  protected LL = LL;

  protected WorkoutsUpdateChoice = WorkoutsUpdateChoice;
  protected NutritionUpdateChoice = NutritionUpdateChoice;

  protected workouts: WorkoutsUpdateChoice = WorkoutsUpdateChoice.Rebuild;
  protected nutrition: NutritionUpdateChoice = NutritionUpdateChoice.Update;

  protected hasTrainingPlanCustomizations = false;
  protected askAboutNutrition = true;
  protected hasAcceptedTarget = false;
  protected targetWouldChange = false;
  protected energyBalanceChanged = false;
  protected goalDirectionChanged = false;
  protected bodyDataChanged = false;
  protected activityLevelChanged = false;

  private acceptedTarget_kcal: number;
  /** absent when the app could not work out what it recommends -- see getUpdateReplacesTargetText */
  private recommendedTarget_kcal: number | undefined;

  /** the block holding every warning and info card, so we can tell whether it has been seen */
  @ViewChild('messagesBlock') private messagesBlock: ElementRef<HTMLElement>;
  /** the fixed footer; its top edge is the fold the message block has to clear */
  @ViewChild('actionButtons', { read: ElementRef }) private actionButtons: ElementRef<HTMLElement>;

  /**
   * Whether the user has had the chance to read the messages below the questions. Continue is
   * held until it is true; Cancel never is.
   *
   * Why it works the way it does — and in particular why the chevron frees Continue before it
   * scrolls rather than after — is in design-docs/nutrition-target-persistence.md, "Warning or
   * info, and making sure either is read".
   */
  protected messagesSeen = false;

  /**
   * How far above the page's bottom edge to float the chevron, in pixels. Measured from the
   * action buttons rather than hard-coded — see the design doc named on `messagesSeen` for why
   * one hard-coded offset cannot be right on both form factors.
   */
  protected scrollHintBottom_px = DEFAULT_SCROLL_HINT_BOTTOM_PX;

  constructor(
    public navCtrl: NavController,
    private settings: SettingsService,
    private cd: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnInit() {
    const data = this.navParams.data.local;
    this.hasTrainingPlanCustomizations = data?.hasTrainingPlanCustomizations ?? false;
    this.askAboutNutrition = data?.askAboutNutrition ?? true;
    this.hasAcceptedTarget = data?.hasAcceptedTarget ?? false;
    this.targetWouldChange = data?.targetWouldChange ?? false;
    this.energyBalanceChanged = data?.energyBalanceChanged ?? false;
    this.goalDirectionChanged = data?.goalDirectionChanged ?? false;
    this.bodyDataChanged = data?.bodyDataChanged ?? false;
    this.activityLevelChanged = data?.activityLevelChanged ?? false;
    this.acceptedTarget_kcal = data?.acceptedTarget_kcal;
    this.recommendedTarget_kcal = data?.recommendedTarget_kcal;

    // Keep is the opening answer only when there is a target of the user's own to
    // keep AND keeping it would not silently cancel the change they just made. A
    // moved deficit slider or a flipped goal IS a statement about how much to
    // eat, so opening on Keep there would ask them to undo themselves.
    this.nutrition = this.hasAcceptedTarget && !this.energyBalanceChanged && !this.goalDirectionChanged
      ? NutritionUpdateChoice.Keep
      : NutritionUpdateChoice.Update;
  }

  // -- selection --

  onWorkoutsChange() {
    this.onAnswerChanged();
  }

  onNutritionChange() {
    this.onAnswerChanged();
  }

  /**
   * A changed answer means a different set of messages, so the user has not seen THIS set yet.
   * Re-arming and then re-measuring after layout settles keeps the desktop case silent: there
   * the modal sizes to its content, the block is already on screen, and it is marked seen again
   * before anyone notices the button flicker.
   */
  private onAnswerChanged() {
    this.messagesSeen = false;
    this.safeMarkForCheck(this.cd);
    this.safeTimeout(() => this.remeasureFold(), 0);
  }

  // -- warnings --

  /** the user is keeping a calorie target that their new deficit or surplus will not reach */
  showKeepEnergyBalanceWarning(): boolean {
    return this.nutrition === NutritionUpdateChoice.Keep && this.energyBalanceChanged;
  }

  /** the user is keeping a target that was worked out for the goal they just left */
  showKeepGoalWarning(): boolean {
    return this.nutrition === NutritionUpdateChoice.Keep && this.goalDirectionChanged;
  }

  /** the user is keeping a target that was worked out from the body data they just replaced */
  showKeepBodyDataWarning(): boolean {
    return this.nutrition === NutritionUpdateChoice.Keep && this.bodyDataChanged;
  }

  /**
   * The user is keeping a target worked out for how active they used to be.
   *
   * Activity level feeds the maintenance calories the target is built from, so changing it and
   * then keeping the old target is a contradiction — allowed, but worth saying out loud, since
   * the user presumably changed it expecting their food to follow.
   */
  showKeepActivityLevelWarning(): boolean {
    return this.nutrition === NutritionUpdateChoice.Keep && this.activityLevelChanged;
  }

  /**
   * The user is about to lose a calorie target they set themselves.
   *
   * A rebuild always works the target out afresh, so it always replaces it. On the two
   * kept-workouts answers it is only replaced when something that feeds the target
   * changed — otherwise there is nothing to warn about and naming a replacement number
   * would be false.
   */
  showUpdateReplacesTargetWarning(): boolean {
    if (this.nutrition !== NutritionUpdateChoice.Update || !this.hasAcceptedTarget) {
      return false;
    }
    return this.workouts === WorkoutsUpdateChoice.Rebuild || this.targetWouldChange;
  }

  // -- making sure the messages are actually read --

  /**
   * Any warning or info card is on screen.
   *
   * The `askAboutNutrition` term is not redundant: every card lives inside that block in the
   * template, so without it a caller could hold Continue over a card that is not rendered and
   * a block that does not exist. Today only coach mode passes false, and it passes every change
   * flag false too — but that makes the invariant the caller's, not this component's.
   */
  hasMessages(): boolean {
    if (!this.askAboutNutrition) {
      return false;
    }
    return this.showKeepEnergyBalanceWarning()
      || this.showKeepGoalWarning()
      || this.showKeepBodyDataWarning()
      || this.showKeepActivityLevelWarning()
      || this.showUpdateReplacesTargetWarning();
  }

  /** Continue waits until the messages have been seen; Cancel never does */
  isContinueBlocked(): boolean {
    return this.hasMessages() && !this.messagesSeen;
  }

  /** the chevron shows exactly when the button is held, so the way out is always visible */
  showScrollHint(): boolean {
    return this.isContinueBlocked();
  }

  handleScrollToMessages() {
    // acknowledge BEFORE scrolling — the tap is the acknowledgement, the scroll only a
    // courtesy. See the design doc named on `messagesSeen`.
    this.messagesSeen = true;
    this.safeMarkForCheck(this.cd);
    this.messagesBlock?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }

  onContentScroll() {
    this.remeasureFold();
  }

  ngAfterViewInit() {
    // The action buttons position themselves asynchronously: their own ngAfterViewInit awaits a
    // change-detection pass AND a drawn frame before deciding where to sit, and until then they
    // can be parked off-screen. A zero-delay timer always fires first, so measuring the fold
    // then would read a bar that has not settled — and because "seen" latches for the visit, a
    // fold read too low would free Continue permanently over an unread card. Waiting a frame
    // puts this after their positioning; `isFoldMeasurable` catches the case where it does not.
    this.safeTimeout(() => {
      requestAnimationFrame(() => this.remeasureFold());
    }, 0);
    // and once more after the bar's slide-in can have finished. Without this the first
    // measurement is refused on every phone visit — correctly, but nothing else re-measures
    // when the page is too short to scroll, so the user would be asked to acknowledge a card
    // that was on screen all along. A late measurement can only move the answer to "seen",
    // never back, so it cannot re-hold a freed button.
    this.safeTimeout(() => this.remeasureFold(), getTransitionSlideInOutMs() + SETTLE_GRACE_MS);
  }

  onWindowResize() {
    // covers rotation and a keyboard opening, which move the fold under a decision already made
    this.remeasureFold();
  }

  /**
   * Re-measure everything the fold decides: where the chevron floats, and whether the message
   * cards count as seen. Every trigger wants both, so they are called together here rather than
   * remembered separately at each site.
   *
   * They stay two functions rather than one because the offset has to stay current even after
   * the messages are marked seen, and `refreshMessagesSeen` returns early once they are.
   */
  private remeasureFold() {
    this.refreshScrollHintOffset();
    this.refreshMessagesSeen();
  }

  /** Keep the chevron floating just above the action buttons. */
  private refreshScrollHintOffset() {
    const height = this.actionButtons?.nativeElement?.getBoundingClientRect().height ?? 0;
    if (height > 0) {
      this.scrollHintBottom_px = Math.round(height) + SCROLL_HINT_GAP_PX;
    }
  }

  /** mark the messages seen once the whole block has cleared the action buttons' top edge */
  private refreshMessagesSeen() {
    if (this.messagesSeen || !this.hasMessages()) {
      return;
    }
    const block = this.messagesBlock?.nativeElement;
    const buttons = this.actionButtons?.nativeElement;
    // No trustworthy measurement means no decision, and the decision is deliberately left at
    // "not seen": the cost is a chevron the user did not need, which one tap clears. Guessing
    // the other way would silently free Continue over a message nobody read, which is the whole
    // thing this exists to prevent.
    if (!block || !buttons || !this.isFoldMeasurable(buttons)) {
      return;
    }
    // The buttons sit BELOW the scrolling content — overlaying it on a phone, in flow inside
    // the modal on a desktop — so their top edge is the fold in both cases. A card behind or
    // beneath them has not been seen.
    const fold = buttons.getBoundingClientRect().top;
    if (block.getBoundingClientRect().bottom <= fold) {
      this.messagesSeen = true;
      this.safeMarkForCheck(this.cd);
    }
  }

  /**
   * Whether the action buttons are where they will finally sit, so their top edge is a real
   * fold rather than a transient one.
   *
   * On a phone the bar is fixed to the bottom and slides up from `translateY(100%)`, so its
   * BOTTOM edge is what gives it away: a full bar-height below the window while parked, still
   * below it at every point during the slide, and level with it once settled. Testing the top
   * edge alone only catches the parked instant and lets the whole slide through — which reads
   * the fold too low, the one direction this must never guess, and "seen" never un-latches.
   *
   * On a desktop modal the bar is in flow inside the modal, so its bottom sits above the
   * window and the test passes, as it should. The one pixel of slack absorbs sub-pixel layout.
   */
  private isFoldMeasurable(buttons: HTMLElement): boolean {
    const rect = buttons.getBoundingClientRect();
    return rect.height > 0
      && rect.top < window.innerHeight
      && rect.bottom <= window.innerHeight + 1;
  }

  // -- text that depends on what the user changed --

  /** the rebuild note only mentions losing customizations when there are some to lose */
  getWorkoutsRebuildNote(): string {
    return this.hasTrainingPlanCustomizations
      ? LL.intake.updateOptions.workoutsRebuildNoteWithCustomizations()
      : LL.intake.updateOptions.workoutsRebuildNote();
  }

  /** the keep note names the target only when there is one of the user's own to name */
  getNutritionKeepNote(): string {
    return this.hasAcceptedTarget
      ? LL.intake.updateOptions.nutritionKeepNote({ target: this.getAcceptedTargetText() })
      : LL.intake.updateOptions.nutritionKeepNoteNoTarget();
  }

  /**
   * The warning that answering Update replaces the target the user typed in.
   *
   * It names the replacement figure only when the app actually worked one out. When the re-solve
   * behind that figure could not be completed, the caller passes nothing rather than a number it
   * did not compute (df-f3070b96b839), and the user still gets the warning -- without a figure
   * that would be wrong.
   */
  getUpdateReplacesTargetText(): string {
    return this.recommendedTarget_kcal === undefined
      ? LL.intake.updateOptions.warnUpdateReplacesTargetUnknown({ target: this.getAcceptedTargetText() })
      : LL.intake.updateOptions.warnUpdateReplacesTarget({
        target: this.getAcceptedTargetText(),
        recommended: this.formatKcal(this.recommendedTarget_kcal),
      });
  }

  // -- formatted numbers --

  getAcceptedTargetText(): string {
    return this.formatKcal(this.acceptedTarget_kcal);
  }

  // -- navigation --

  handleContinue(event: Event) {
    lockClick(event, this.handleContinueAsync());
  }

  private async handleContinueAsync() {
    const result: UpdatePlanOptionsPageResult = {
      workouts: this.workouts,
      nutrition: this.nutrition,
    };
    await this.navCtrl.popAsync(result);
  }

  handleCancel(event: Event) {
    lockClick(event, this.handleCancelAsync());
  }

  /**
   * Cancel means the save does not happen at all, so the caller is answered with
   * nothing and goes back to the form with the user's edits still on screen.
   *
   * This is deliberately unlike the alert it replaces, where dismissing counted
   * as "keep my current program" and quietly went ahead with the save.
   */
  private async handleCancelAsync() {
    await this.navCtrl.popAsync();
  }

  // -- private helpers --

  private formatKcal(value: number): string {
    return formatMeasure(Math.round(value ?? 0), 'nutrient_kcal', this.settings.locale, 'kcal');
  }
}
